import React from "react";
import { NeoInput } from "../../../../../components/Common";
import BlockHeader, { getMethodConfig } from "./BlockHeader";
import SetsTable from "./SetsTable";
import SwimBlockSummary from "../swimming/SwimBlockSummary";
import WodBlockSummary from "../wod/WodBlockSummary";
import RunningBlockSummary from "../running/RunningBlockSummary";
import { getScoreTypeForWodFormat, WOD_COMPONENT_TYPES, WOD_FORMATS, WOD_SCORE_TYPES } from "../../domain/wod/constants";
import { RUNNING_BLOCK_TYPES, RUNNING_LOCATION_TYPES, RUNNING_SESSION_TYPES, RUNNING_TERRAIN_TYPES } from "../../domain/running/constants";

/**
 * Container de um bloco de treino individual.
 * Compõe: BlockHeader + painel de configuração por modalidade + SetsTable.
 *
 * @param {Object}   block             - Dados do bloco (formik values)
 * @param {number}   blockIndex        - Índice do bloco na lista
 * @param {string}   namePrefix        - Prefixo formik (e.g. "blocks")
 * @param {string}   defaultWorkoutType - Tipo de treino padrão
 * @param {Function} handleChange      - Formik handleChange
 * @param {Function} setFieldValue     - Formik setFieldValue
 * @param {Array}    visibleWorkoutTypes - Tipos de treino visíveis
 * @param {Function} onDuplicate       - Callback p/ duplicar bloco
 * @param {Function} onDelete          - Callback p/ remover bloco
 * @param {Object}   libraryHandlers   - Callbacks da lib (save, duplicate, replace)
 * @param {Array}    runningPaceGroups - Grupos de pace para corrida
 */
const BlockCard = ({
    block,
    blockIndex,
    namePrefix,
    defaultWorkoutType = "strength",
    handleChange,
    setFieldValue,
    visibleWorkoutTypes,
    onDuplicate,
    onDelete,
    libraryHandlers,
    runningPaceGroups = [],
}) => {
    const blockWorkoutType = block.workoutType || defaultWorkoutType || "strength";
    const method = getMethodConfig(block.method || "normal");
    const isStrength = blockWorkoutType === "strength";
    const isSwimming = blockWorkoutType === "swimming";
    const isWod = blockWorkoutType === "wod";
    const isRunning = blockWorkoutType === "running";

    return (
        <div className="neo-block-card mb-0 overflow-hidden">
            {/* ── Barra lateral colorida do método ── */}
            {isStrength && block.method && block.method !== "normal" && (
                <div
                    className="block-method-indicator"
                    style={{ "--method-color": method.color }}
                />
            )}

            <div className="p-3">
                {/* ── Header do bloco ── */}
                <BlockHeader
                    block={block}
                    blockIndex={blockIndex}
                    namePrefix={namePrefix}
                    blockWorkoutType={blockWorkoutType}
                    handleChange={handleChange}
                    visibleWorkoutTypes={visibleWorkoutTypes}
                    onDuplicate={onDuplicate}
                    onDelete={onDelete}
                    onSaveWodBenchmark={libraryHandlers?.handleSaveWodBenchmark}
                    onSaveRunningSession={libraryHandlers?.handleSaveRunningSession}
                />

                {/* ── Observações / Objetivos do Bloco ── */}
                <div className="mb-3">
                    <NeoInput
                        type="text"
                        name={`${namePrefix}.${blockIndex}.description`}
                        value={block.description || ""}
                        onChange={handleChange}
                        placeholder="Objetivos / Observações deste bloco (Opcional)"
                        className="block-description-input"
                        style={{ marginBottom: 0 }}
                    />
                </div>

                {/* ── Badge do método ── */}
                {isStrength && block.method && block.method !== "normal" && (
                    <div className="mb-3">
                        <span
                            className="d-inline-flex align-items-center gap-1 block-method-badge font-size-11 px-2 py-1 fw-medium"
                            style={{
                                "--method-color": method.color,
                                "--method-bg-color": `${method.color}15`,
                                "--method-border-color": `${method.color}30`
                            }}
                        >
                            <i className="mdi mdi-link-variant font-size-12" />
                            {method.label}
                            <span className="font-size-10 fw-normal ms-1 opacity-75">
                                — {method.desc}
                            </span>
                        </span>
                    </div>
                )}

                {/* ── Sumário e painel de Swimming ── */}
                {isSwimming && (
                    <div className="mb-3">
                        <SwimBlockSummary block={block} />
                    </div>
                )}

                {/* ── Sumário e painel de WOD ── */}
                {isWod && (
                    <>
                        <div className="mb-3">
                            <WodBlockSummary block={block} />
                        </div>
                        <div className="mb-3 p-3 bg-light-subtle border rounded">
                            <div className="row g-2">
                                <div className="col-md-3 col-6">
                                    <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Componente</label>
                                    <NeoInput
                                        type="select"
                                        name={`${namePrefix}.${blockIndex}.componentType`}
                                        value={block.componentType || "metcon"}
                                        onChange={handleChange}
                                        size="sm"
                                    >
                                        {WOD_COMPONENT_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </NeoInput>
                                 </div>
                                <div className="col-md-3 col-6">
                                    <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Formato</label>
                                    <NeoInput
                                        type="select"
                                        name={`${namePrefix}.${blockIndex}.format`}
                                        value={block.format || "amrap"}
                                        onChange={(event) => {
                                            handleChange(event);
                                            setFieldValue?.(`${namePrefix}.${blockIndex}.scoreType`, getScoreTypeForWodFormat(event.target.value));
                                        }}
                                        size="sm"
                                    >
                                        {WOD_FORMATS.map(format => (
                                            <option key={format.value} value={format.value}>{format.label}</option>
                                        ))}
                                    </NeoInput>
                                </div>
                                <div className="col-md-2 col-6">
                                    <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Score</label>
                                    <NeoInput
                                        type="select"
                                        name={`${namePrefix}.${blockIndex}.scoreType`}
                                        value={block.scoreType || getScoreTypeForWodFormat(block.format)}
                                        onChange={handleChange}
                                        size="sm"
                                    >
                                        {WOD_SCORE_TYPES.map(score => (
                                            <option key={score.value} value={score.value}>{score.label}</option>
                                        ))}
                                    </NeoInput>
                                </div>
                                <div className="col-md-2 col-6">
                                    <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Time cap</label>
                                    <NeoInput
                                        type="text"
                                        name={`${namePrefix}.${blockIndex}.timeCap`}
                                        value={block.timeCap || ""}
                                        onChange={handleChange}
                                        size="sm"
                                        placeholder="20 min"
                                    />
                                </div>
                                <div className="col-md-2 col-6">
                                    <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Rounds</label>
                                    <NeoInput
                                        type="text"
                                        name={`${namePrefix}.${blockIndex}.rounds`}
                                        value={block.rounds || ""}
                                        onChange={handleChange}
                                        size="sm"
                                        placeholder="5"
                                    />
                                </div>
                                <div className="col-md-6 col-12">
                                    <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Estímulo</label>
                                    <NeoInput
                                        type="text"
                                        name={`${namePrefix}.${blockIndex}.stimulus`}
                                        value={block.stimulus || ""}
                                        onChange={handleChange}
                                        size="sm"
                                        placeholder="Curto e intenso, transições rápidas, manter ritmo constante..."
                                    />
                                </div>
                                <div className="col-md-6 col-12">
                                    <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Scaling geral</label>
                                    <NeoInput
                                        type="text"
                                        name={`${namePrefix}.${blockIndex}.scalingNotes`}
                                        value={block.scalingNotes || ""}
                                        onChange={handleChange}
                                        size="sm"
                                        placeholder="Reduzir reps/carga, trocar barra por ring row, etc."
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* ── Sumário e painel de Running ── */}
                {isRunning && (
                    <>
                        <div className="mb-3">
                            <RunningBlockSummary block={block} />
                        </div>
                        <div className="mb-3 p-3 bg-light-subtle border rounded">
                            <div className="row g-2">
                                <div className="col-md-3 col-6">
                                    <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Tipo da sessão</label>
                                    <NeoInput
                                        type="select"
                                        name={`${namePrefix}.${blockIndex}.sessionType`}
                                        value={block.sessionType || "easy"}
                                        onChange={handleChange}
                                        size="sm"
                                    >
                                        {RUNNING_SESSION_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </NeoInput>
                                </div>
                                <div className="col-md-3 col-6">
                                    <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Bloco</label>
                                    <NeoInput
                                        type="select"
                                        name={`${namePrefix}.${blockIndex}.blockType`}
                                        value={block.blockType || "main"}
                                        onChange={handleChange}
                                        size="sm"
                                    >
                                        {RUNNING_BLOCK_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </NeoInput>
                                </div>
                                <div className="col-md-2 col-6">
                                    <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Repetir bloco</label>
                                    <NeoInput
                                        type="number"
                                        name={`${namePrefix}.${blockIndex}.repeat`}
                                        value={block.repeat || 1}
                                        onChange={handleChange}
                                        size="sm"
                                        min="1"
                                    />
                                </div>
                                <div className="col-md-4 col-6">
                                    <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Recuperação entre repetições</label>
                                    <NeoInput
                                        type="text"
                                        name={`${namePrefix}.${blockIndex}.restBetweenRepeats`}
                                        value={block.restBetweenRepeats || ""}
                                        onChange={handleChange}
                                        size="sm"
                                        placeholder="Ex: 2min trote"
                                    />
                                </div>
                                <div className="col-md-3 col-6">
                                    <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Local</label>
                                    <NeoInput
                                        type="select"
                                        name={`${namePrefix}.${blockIndex}.locationType`}
                                        value={block.locationType || "street"}
                                        onChange={handleChange}
                                        size="sm"
                                    >
                                        {RUNNING_LOCATION_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </NeoInput>
                                </div>
                                <div className="col-md-3 col-6">
                                    <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Terreno padrão</label>
                                    <NeoInput
                                        type="select"
                                        name={`${namePrefix}.${blockIndex}.terrain`}
                                        value={block.terrain || "flat"}
                                        onChange={handleChange}
                                        size="sm"
                                    >
                                        {RUNNING_TERRAIN_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </NeoInput>
                                </div>
                                <div className="col-md-6 col-12">
                                    <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Rota / ponto de encontro</label>
                                    <NeoInput
                                        type="text"
                                        name={`${namePrefix}.${blockIndex}.routeName`}
                                        value={block.routeName || ""}
                                        onChange={handleChange}
                                        size="sm"
                                        placeholder="Ex: Pista, orla, parque, praça..."
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* ── Lista de Exercícios/Séries ── */}
                <SetsTable
                    block={block}
                    blockIndex={blockIndex}
                    namePrefix={namePrefix}
                    blockWorkoutType={blockWorkoutType}
                    method={method}
                    handleChange={handleChange}
                    duplicateSet={libraryHandlers?.duplicateSet}
                    replaceStrengthExercise={libraryHandlers?.replaceStrengthExercise}
                    handleSaveSwimSet={libraryHandlers?.handleSaveSwimSet}
                    handleSaveStrengthExercise={libraryHandlers?.handleSaveStrengthExercise}
                    runningPaceGroups={runningPaceGroups}
                />
            </div>
        </div>
    );
};

export default BlockCard;
